import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shown app-wide when an authenticated request returns 401 (session expired or
 * invalidated by a deploy). Gives the user a clear prompt to sign in again
 * instead of a cryptic inline error on the action they were performing.
 */
export function SessionExpiredModal() {
  const { sessionExpired, dismissSessionExpired } = useAuth();
  const navigate = useNavigate();

  if (!sessionExpired) return null;

  const handleSignIn = () => {
    dismissSessionExpired();
    navigate('/login', { replace: true });
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop — non-dismissible: the user must sign in again */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Panel */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-describedby="session-expired-desc"
        className="relative bg-white rounded-xl border border-gray-100 shadow-xl w-full max-w-sm mx-4 overflow-hidden"
      >
        <div className="px-6 py-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
          </div>
          <h2 id="session-expired-title" className="text-base font-semibold text-gray-900">
            Your session has ended
          </h2>
          <p id="session-expired-desc" className="mt-2 text-sm text-gray-600">
            You&apos;ve been signed out, either because your session expired or the app was updated.
            Please sign in again to continue.
          </p>
          <button
            type="button"
            onClick={handleSignIn}
            autoFocus
            className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Sign in again
          </button>
        </div>
      </div>
    </div>
  );
}
